import { useEffect, useMemo, useRef, useState } from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    EmptyTableBox,
    TabSpec,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";
import { Empty, RadioChangeEvent } from "antd";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";

import { BlessedColumnDeltaOption } from "api/generated/model";

import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import * as T from "../types";

import { RiskHeatMapInner } from "./components/RiskHeatmap";
import {
    INHERENT_MODE,
    MapMode,
    RESIDUAL_MODE,
} from "./components/RiskHeatmapTypes";
import { RiskMapAISummary } from "./components/RiskMapAISummary";
import { MapInsightsFilterBar } from "./MapInsightsFilterBar";
import {
    buildHeatMapData,
    getFocusFilterValue,
    toggleFocusPair,
} from "./mapInsightsHeatmap";
import { MAP_INSIGHTS_COLUMN_IDS } from "./mapInsightsModel";
import { useMapInsightsTable } from "./mapInsightsTable";
import {
    extractDeltaValue,
    extractValues,
    normalizeNullableDelta,
    getTitle,
    useMapInsightsParams,
} from "./mapInsightsUtils";
import { useInsightsSuspenseQuery } from "./queries/useInsightsQuery";
import * as theme from "./theme.css";

interface MapInsightsProps {
    start: moment.Moment;
    end: moment.Moment;
}

interface Axis {
    values: string[];
    caption: string;
}

// The heatmap can only be drawn against a single, stable scale. When the
// likelihood/impact options change within the selected period the two ends
// aren't comparable on one map, so we surface that as a distinct state
// instead of silently rendering against the end scale.
type MapInsightsAxes =
    | { kind: "Changed" }
    | {
          kind: "Unchanged";
          xAxis: Axis;
          yAxis: Axis;
          xToEnum: (x: string) => number;
          yToEnum: (y: string) => number;
          likelihoodOptions: BlessedColumnDeltaOption[];
          impactOptions: BlessedColumnDeltaOption[];
      };

export function MapInsights({ start, end }: MapInsightsProps) {
    const { id, entityId } = useMapInsightsParams();
    const { data } = useInsightsSuspenseQuery(id, entityId, start, end);
    const [mapMode, setMapMode] = useState<MapMode>("inherent");
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [aiSummaryEnabled, setAiSummaryEnabled] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Collapses AI summary on date change
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => setAiSummaryEnabled(false), [start, end]);

    const likelihoodCol =
        mapMode === INHERENT_MODE
            ? data.linkedResources.blessedColumns.inherentLikelihood
            : data.linkedResources.blessedColumns.residualLikelihood;
    const impactCol =
        mapMode === INHERENT_MODE
            ? data.linkedResources.blessedColumns.inherentImpact
            : data.linkedResources.blessedColumns.residualImpact;

    const axes = useMemo((): MapInsightsAxes => {
        if (
            likelihoodCol.options.type === "Changed" ||
            impactCol.options.type === "Changed"
        ) {
            return { kind: "Changed" };
        }

        const likelihoodOptions = likelihoodCol.options.value;
        const impactOptions = impactCol.options.value;

        return {
            kind: "Unchanged",
            xAxis: {
                values: likelihoodOptions.map((o) => o.label),
                caption: extractDeltaValue(likelihoodCol.name),
            },
            yAxis: {
                values: impactOptions.map((o) => o.label),
                caption: extractDeltaValue(impactCol.name),
            },
            xToEnum: (x: string) =>
                likelihoodOptions.findIndex((o) => o.label === x),
            yToEnum: (y: string) =>
                impactOptions.findIndex((o) => o.label === y),
            likelihoodOptions,
            impactOptions,
        };
    }, [likelihoodCol, impactCol]);

    const { table, getFilterComponent } = useMapInsightsTable(
        data,
        mapMode,
        id,
        entityId
    );

    const residualDataAvailable = useMemo(
        () =>
            data.content.resource.some(
                (assessment) =>
                    extractValues(
                        normalizeNullableDelta(assessment.residualImpact)
                    ).some((value) => value !== null) &&
                    extractValues(
                        normalizeNullableDelta(assessment.residualLikelihood)
                    ).some((value) => value !== null)
            ),
        [data]
    );

    const heatMapData = buildHeatMapData(table, mapMode);

    const tableRef = useRef<HTMLTableElement>(null);

    const tabSpecs: TabSpec[] = [
        {
            id: T.RISK_MAP_TAB,
            label: t("RiskDirectory_View_Tab_Risk_Map"),
            onClick: () => {
                const params = new URLSearchParams(window.location.search);
                navigate({
                    search: params.toString(),
                    hash: T.RISK_MAP_TAB,
                });
            },
        },
        {
            id: T.RISK_MAP_INSIGHTS_TAB,
            label: t("RiskDirectory_View_Tab_Risk_Insights"),
            onClick: () => {},
        },
        {
            id: T.RISK_MAP_HISTORY_TAB,
            label: t("RiskDirectory_View_Tab_Risk_Map_History"),
            onClick: () => {
                const params = new URLSearchParams(window.location.search);
                navigate({
                    search: params.toString(),
                    hash: T.RISK_MAP_HISTORY_TAB,
                });
            },
        },
    ];
    const navigation = {
        type: "Tabs" as const,
        currentTabId: T.RISK_MAP_INSIGHTS_TAB,
        tabSpecs,
    };

    return (
        <OrgApplicationBar
            contentClassName={theme.riskMapContent}
            title={getTitle(data.linkedResources, id, entityId, t)}
            navigation={navigation}
        >
            <MapInsightsFilterBar
                table={table}
                data={data}
                start={start}
                end={end}
                getFilterComponent={getFilterComponent}
                impactColName={extractDeltaValue(impactCol.name)}
                likelihoodColName={extractDeltaValue(likelihoodCol.name)}
                // Those options are used for heatmap filtering, which is not supported in "Changed" case
                impactOptions={
                    axes.kind === "Unchanged" ? axes.impactOptions : []
                }
                likelihoodOptions={
                    axes.kind === "Unchanged" ? axes.likelihoodOptions : []
                }
            />
            {axes.kind === "Unchanged" ? (
                <>
                    <RiskHeatMapInner
                        xAxis={axes.xAxis}
                        yAxis={axes.yAxis}
                        xToEnum={axes.xToEnum}
                        yToEnum={axes.yToEnum}
                        heatMapData={heatMapData}
                        showHeatmap={showHeatmap}
                        toggleHeatmap={() => setShowHeatmap((v) => !v)}
                        mapMode={mapMode}
                        onMapModeChange={(e: RadioChangeEvent) => {
                            setMapMode(e.target.value as MapMode);
                            setAiSummaryEnabled(false);
                        }}
                        onHeatmapClick={(x: number, y: number) => {
                            const focusColumn = table.getColumn(
                                MAP_INSIGHTS_COLUMN_IDS.FOCUS
                            );
                            if (!focusColumn) {
                                return;
                            }
                            focusColumn.setFilterValue(
                                toggleFocusPair(
                                    getFocusFilterValue(table),
                                    x,
                                    y
                                )
                            );
                        }}
                    />
                    <RiskMapAISummary
                        key={`${start.format("YYYY-MM-DD")}/${end.format("YYYY-MM-DD")}`}
                        riskDirectoryId={id}
                        kind={
                            mapMode === RESIDUAL_MODE ? "Residual" : "Inherent"
                        }
                        from={start.format("YYYY-MM-DD")}
                        to={end.format("YYYY-MM-DD")}
                        enabled={aiSummaryEnabled}
                        onEnable={() => setAiSummaryEnabled(true)}
                        dataUnavailable={
                            mapMode === RESIDUAL_MODE && !residualDataAvailable
                        }
                    />
                </>
            ) : (
                <EmptyTableBox>
                    <Empty
                        className={"ant-empty-normal"}
                        image={
                            <ExclamationCircleOutlined
                                className={theme.insightScaleChangedIcon}
                            />
                        }
                        description={t("RiskInsights_Error_ScaleChanged")}
                    />
                </EmptyTableBox>
            )}
            <div>
                <TSTTable
                    tableRef={tableRef}
                    table={table}
                    noResultsFallback={t("RiskDirectory_Insights_NoResults")}
                    noSourceRowsFallback={t("RiskDirectory_Insights_NoData")}
                />
                <TSTPagination
                    table={table}
                    tableElement={tableRef}
                    pageLabel={t("Pagination_Page")}
                    getRowCountLabel={(shown, count) =>
                        t("Pagination_RowCount", { shown, count })
                    }
                    getSinglePageRowCountLabel={(count) =>
                        t("Pagination_RowCountSinglePage", { count })
                    }
                />
            </div>
        </OrgApplicationBar>
    );
}
